/**
 * Vocabulary guard for every sentence the product shows a human.
 *
 * It began as the second of two nets. The first — an ID-gated claims renderer that made an
 * unlicensed sentence about a child literally unrenderable — went away with the parent report
 * (ADR-011), because the product no longer describes a child's mind to their family. It
 * reports work done in a classroom to the teacher who set it.
 *
 * So this is now the only net, and its list got shorter on purpose. Two kinds of term were
 * removed:
 *
 *   - **Comparison words** (percent, rank, average). A teacher comparing children in their own
 *     class against each other on work they watched them do is describing their room. That is
 *     a different act from placing a child on a national distribution that does not exist.
 *   - **Learning words** (improvement, strengthening, assessment). They were banned because a
 *     measurement product must not claim it raises a capacity. A teaching product's entire
 *     purpose is that the child gets better at the thing, and copy that cannot say so cannot
 *     describe what the product does.
 *
 * What stays is what no amount of pivoting licenses: claims about a child's nature, clinical
 * language, percentiles against absent norms, and predictions about their future.
 *
 * **What it cannot catch:** implicature. "Sara found the hardest patterns easy" carries no
 * banned word and is a trait claim in everything but grammar. Nothing substring-based will ever
 * catch that, and pretending otherwise would be worse than having no guard, because it would
 * license skipping review. That is caught by review of the copy file, and by nothing else now.
 */
export const FORBIDDEN_TERMS = [
    // Giftedness — the strongest demand driver in this market and prohibited anyway.
    // This pair is the Claim Paradox at its sharpest, resolved against the demand.
    { term: "تیزهوش", match: "substring", why: "giftedness / SAMPAD readiness (lexicon §D)" },
    { term: "سمپاد", match: "substring", why: "the same prohibition, reached by institution name" },
    { term: "نابغه", match: "substring", why: "genius — a trait claim of the strongest kind" },
    { term: "نخبه", match: "substring", why: "elite — a claim of rank among children" },
    // Intelligence. We observe performance on our own tasks; that is not intelligence,
    // and the measurement ceiling (~10–25% of criterion variance) is why.
    { term: "هوش", match: "substring", why: "intelligence — never claimed, at any strength" },
    { term: "آی‌کیو", match: "substring", why: "IQ, transliterated" },
    // The verdict the product pivoted away from (V0 = PIVOT). Banning the word in the
    // UI while the repository is named after it is intentional: the pivot was in the
    // promise, not the codebase. Nothing on a teacher's screen needs the word: a class
    // roster is about what was practised, not about who has a gift for it.
    { term: "استعداد", match: "substring", why: "the talent verdict — outside what evidence licenses" },
    // Clinical. The declared posture is "developmental app, not assessment, not clinical
    // decision support" (v2 §31); a disorder mention silently relocates the product
    // across that boundary and into a regulated category.
    { term: "اختلال", match: "substring", why: "disorder — a clinical claim" },
    { term: "بیش‌فعال", match: "substring", why: "ADHD in colloquial Persian" },
    { term: "اوتیسم", match: "substring", why: "autism — naming a condition we cannot assess" },
    { term: "نارساخوان", match: "substring", why: "dyslexia — naming a condition we cannot assess" },
    { term: "غربالگری", match: "substring", why: "screening of any kind" },
    {
        term: "قضاوت",
        match: "substring",
        why: "judgement — the product describes what a child did and never judges them",
    },
    // "تشخیص" alone is absent on purpose: in Persian it means both "diagnosis" and
    // "recognition", so banning it would flag a truthful description of what a matching
    // task asks a child to do. Every clinical use of it needs a disorder name, which the
    // entries above already catch.
    //
    // "ارزیابی" (assessment) was HERE and is now allowed. It was banned because the report
    // carried a footer saying the product is not an assessment, and copy offering to assess a
    // child contradicted it one scroll away. A teacher checking whether their class can add to
    // ten is assessing them, in the ordinary classroom sense of the word, and a product that
    // cannot say so has to talk around the thing it does (ADR-011).
    // Norms. Within-class comparison is now allowed (ADR-011); a distribution over Persian-
    // speaking children still does not exist, so a percentile remains a fabricated number
    // however it is hedged (v2 §15). That distinction is the whole of what is left here.
    { term: "صدک", match: "substring", why: "percentile — no local norms exist for it to be a percentile OF" },
    // "درصد" and the percent signs were banned as "a score out of 100 without local norms".
    // Proportion correct in a game the teacher assigned is not a norm-referenced score, and
    // "eight of ten correct" is the most useful sentence the dashboard can say.
    //
    // "رتبه" (rank), "بالاتر از میانگین" and "پایین‌تر از میانگین" were banned as comparisons
    // against other children. Comparison against the other children in this teacher's own class
    // is the feature (R-4). Against a population, it is still unsayable — but the barrier for
    // that is the absence of the data, and "صدک" above is what catches an attempt to fake it.
    // Causal and promissory. An outcome promise is still an outcome promise.
    { term: "تضمین", match: "substring", why: "guarantee — an outcome promise" },
    // "بهبود" (improvement) and "تقویت" (strengthening) were banned as causal claims that the
    // product raises a capacity — correct for a measurement instrument, which merely observed.
    // A teaching product exists in order to make that claim about the skill it teaches, and its
    // copy cannot describe itself without the words. The claim is now about a taught skill
    // ("adding to ten got easier"), never about a faculty, and no substring rule can tell those
    // apart — the terms above that name faculties are what hold that line.
    // Typing and prediction.
    { term: "سبک یادگیری", match: "substring", why: "learning-style typing" },
    { term: "آینده شغلی", match: "substring", why: "career prediction" },
    { term: "رشته تحصیلی", match: "substring", why: "subject-field prediction" },
    // Latin. The UI is Persian, so these reach a screen mainly through an alt text, an
    // aria-label or a stray untranslated string. "talent" is absent on purpose: it is
    // the workspace name, and banning it would flag every import statement.
    { term: "IQ", match: "word", why: "intelligence quotient — never claimed at any strength" },
    { term: "gifted", match: "word", why: "giftedness — the prohibition of lexicon §D in English" },
    { term: "percentile", match: "word", why: "a rank with no norms behind it to license it" },
    { term: "ADHD", match: "word", why: "a disorder mention crosses the clinical boundary" },
    { term: "autism", match: "word", why: "a disorder mention crosses the clinical boundary" },
    { term: "dyslexia", match: "word", why: "a disorder mention crosses the clinical boundary" },
    { term: "disorder", match: "word", why: "any disorder mention crosses the clinical boundary" },
];
/**
 * Fold away the differences that let the same Persian word be written several ways.
 *
 * Without this the guard is trivially evaded by accident. Text typed on an Arabic
 * keyboard uses ARABIC YEH and ARABIC KAF, which are different codepoints from the
 * Persian letters — "بيش‌فعال" would sail past a match on "بیش‌فعال". The zero-width
 * non-joiner is optional in practice, so "بیشفعال" is the same word to a reader and a
 * different string to `includes`.
 */
function normalize(text) {
    return text
        .replace(/‌/g, "") // ZWNJ — optional in practice, so not part of the word
        .replace(/[يى]/g, "ی") // Arabic yeh, alef maksura → Farsi yeh
        .replace(/ك/g, "ک") // Arabic kaf → Farsi keheh
        .replace(/[ً-ْ]/g, "") // harakat: optional diacritics
        .toLowerCase();
}
const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Persian, Arabic and their presentation forms — the letters a word can be built from. */
const PERSIAN_LETTER = "\\u0600-\\u06FF\\uFB50-\\uFDFF\\uFE70-\\uFEFF";
/**
 * Terms are compiled once, not per call — the table is fixed at module load.
 *
 * Persian terms are plain substring matches unless they opt into a word start, which is
 * expressed as "not preceded by a Persian letter" rather than `\b`, since `\b` is
 * defined over ASCII word characters and never fires between Persian letters.
 */
const PREPARED = FORBIDDEN_TERMS.map((entry) => {
    const needle = normalize(entry.term);
    if (entry.match === "word") {
        return { entry, pattern: new RegExp(`\\b${escape(needle)}\\b`) };
    }
    if (entry.requireWordStart) {
        return { entry, pattern: new RegExp(`(?<![${PERSIAN_LETTER}])${escape(needle)}`) };
    }
    return { entry, pattern: new RegExp(escape(needle)) };
});
/** Every prohibited term present in `text`. Empty means the text is clean. */
export function forbiddenTermsIn(text) {
    const haystack = normalize(text);
    return PREPARED.filter(({ pattern }) => pattern.test(haystack)).map(({ entry }) => entry);
}
/**
 * Throws if `text` carries a prohibited term.
 *
 * Called on rendered claims server-side and on the whole parent copy table at client
 * module load. Failing loudly is the specified behaviour: the lexicon requires that an
 * unlicensed sentence be *unrenderable*, so a screen that cannot render honestly must
 * not render at all.
 */
export function assertLexiconSafe(text, where) {
    const hits = forbiddenTermsIn(text);
    if (hits.length === 0)
        return;
    const detail = hits.map((h) => `"${h.term}" (${h.why})`).join("; ");
    throw new Error(`forbidden vocabulary in ${where}: ${detail}`);
}
//# sourceMappingURL=lexicon-guard.js.map